import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../App';

const PrivacyPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'} py-12 px-4`}>
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/')} className="text-purple-500 mb-8 hover:underline">
          ← Back to Home
        </button>

        <div className={`${isDark ? 'bg-[#241651] text-white' : 'bg-[#241651] text-white'} rounded-3xl p-8 md:p-12 shadow-lg`}>
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className={`${isDark ? 'text-purple-300' : 'text-purple-300'} mb-8`}>Last Updated: January 2026</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed mb-4`}>
                KwizHub collects information to provide and improve our services:
              </p>
              <div className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed space-y-3`}>
                <p><strong>Account Information:</strong> Email address, name, profile picture, and authentication credentials (via Google OAuth)</p>
                <p><strong>Content Data:</strong> Quizzes you create, including questions, answers, and associated images or media</p>
                <p><strong>Usage Data:</strong> Information about how you interact with the Service, including quizzes created, games hosted, games played, and scores achieved</p>
                <p><strong>Payment Information:</strong> Processed securely through Paddle and PayPal. We do not store complete payment card details on our servers</p>
                <p><strong>Game Data:</strong> Player nicknames, scores, answer times, and game session information</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. How We Use Your Information</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed mb-2`}>We use collected information to:</p>
              <ul className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed list-disc pl-6 space-y-1`}>
                <li>Provide, maintain, and improve the Service</li>
                <li>Process your subscription and payments</li>
                <li>Generate AI-powered quiz content and images</li>
                <li>Enable real-time multiplayer quiz games</li>
                <li>Calculate and display scores and leaderboards</li>
                <li>Send important updates about your account and the Service</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Detect and prevent fraud, cheating, or abuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Information Sharing</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed mb-2`}>
                We do not sell your personal information. We may share information with:
              </p>
              <div className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed space-y-2`}>
                <p><strong>Service Providers:</strong> Third-party services that help us operate (Paddle/PayPal for payments, OpenAI for AI features, Giphy for images)</p>
                <p><strong>Legal Requirements:</strong> When required by law or to protect our rights</p>
                <p><strong>Public Quizzes:</strong> Quizzes you choose to make public may be visible to other users, including your creator name</p>
                <p><strong>Game Participants:</strong> During live games, player nicknames and scores are visible to all participants</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Data Security</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                We implement appropriate technical and organizational measures to protect your personal information, including encryption of data in transit and at rest. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Your Rights</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed mb-2`}>You have the right to:</p>
              <ul className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed list-disc pl-6 space-y-1`}>
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Export your created quizzes</li>
                <li>Opt-out of marketing communications</li>
                <li>Set quizzes as private to limit visibility</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Cookies and Tracking</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                We use cookies and similar technologies to maintain your session, remember your preferences (such as dark mode), and analyze how you use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Children's Privacy</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                KwizHub is designed for general audiences including educational use. We do not knowingly collect personal information from children under 13 without parental consent. Players joining games only need to provide a nickname, not personal account information. If you believe a child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Third-Party Services</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                Our Service uses third-party AI services (OpenAI) to generate quiz questions and Giphy to provide images. Payment processing is handled by Paddle and PayPal. These services have their own privacy policies governing how they handle data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Data Retention</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                We retain your information for as long as your account is active or as needed to provide services. Game session data may be retained for analytics purposes. You may request deletion of your account and data at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. International Data Transfers</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Changes to Privacy Policy</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through a notice on the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                For questions about this Privacy Policy or our data practices, contact us at:
              </p>
              <a href="mailto:support@kwizhub.com" className="text-purple-500 hover:underline">support@kwizhub.com</a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
